import { notification } from "antd";
import { ErrorCodeDto, Middleware, ResponseContext } from "api/generated/lumen";
import i18n from "app/i18n";
import { store } from "app/store";
import throttle from "lodash.throttle";
import { fetchApiRefreshTokenHandler } from "utils/fetchApiRefreshTokenHandler";

import { globalErrorDisplay } from "utils/globalErrorDisplay";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const exampleError = {
  code: 400,
  errors: [
    {
      errorkey: "error_type:failure.type_field:name_must-be-unique",
      message: ";Failure type Name must be unique",
      propertyname: "name",
    },
  ],
  traceid: null,
  stacktrace:
    "   at Lumen.Application.Common.Validation.Extensions.FailureManagementContextExtensions.AsyncFuncOnSetWithValidation[T](IFailureManagementContext dataContext, T obj, String errorKey, String errorMessage, Func`4 asyncActionOnSet, CancellationToken cancellationToken) in /home/vsts/work/1/s/Backend/Core/Lumen.Application/Common/Validation/Extensions/FailureManagementContextExtensions.cs:line 93\n   at Lumen.Application.Common.Validation.Extensions.FailureManagementContextExtensions.InsertAsyncWithValidation[T](IFailureManagementContext dataContext, T obj, String errorKey, String errorMessage, CancellationToken cancellationToken) in /home/vsts/work/1/s/Backend/Core/Lumen.Application/Common/Validation/Extensions/FailureManagementContextExtensions.cs:line 63\n   at Lumen.Application.Common.Services.BaseCrudService.InsertWithValidationAsync[TDb,TDto](TDto createCommand, CancellationToken cancellationToken, String uniqueViolationErrorKey, String uniqueViolationErrorMessage) in /home/vsts/work/1/s/Backend/Core/Lumen.Application/Common/Services/BaseCrudService.cs:line 245\n   at Lumen.Application.FailureTypes.Commands.CreateFailureType.CreateFailureTypeCommand.CreateFailureTypeCommandHandler.Handle(CreateFailureTypeCommand request, CancellationToken cancellationToken) in /home/vsts/work/1/s/Backend/Core/Lumen.Application/FailureTypes/Commands/CreateFailureType/CreateFailureTypeCommand.Handler.cs:line 28\n   at Lumen.Application.Common.Behaviors.ValidationBehaviour`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next) in /home/vsts/work/1/s/Backend/Core/Lumen.Application/Common/Behaviors/ValidationBehavior.cs:line 33\n   at MediatR.Pipeline.RequestExceptionProcessorBehavior`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next)\n   at MediatR.Pipeline.RequestExceptionProcessorBehavior`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next)\n   at MediatR.Pipeline.RequestExceptionActionProcessorBehavior`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next)\n   at MediatR.Pipeline.RequestExceptionActionProcessorBehavior`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next)\n   at MediatR.Pipeline.RequestPostProcessorBehavior`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next)\n   at MediatR.Pipeline.RequestPreProcessorBehavior`2.Handle(TRequest request, CancellationToken cancellationToken, RequestHandlerDelegate`1 next)\n   at LumenApi.Controllers.Resource.FailureTypesController.Create(CreateFailureTypeCommand command) in /home/vsts/work/1/s/Backend/WebApi/LumenApi/Controllers/Resource/FailureTypesController.cs:line 110\n   at lambda_method720(Closure , Object )\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ActionMethodExecutor.AwaitableObjectResultExecutor.Execute(IActionResultTypeMapper mapper, ObjectMethodExecutor executor, Object controller, Object[] arguments)\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ControllerActionInvoker.<InvokeActionMethodAsync>g__Awaited|12_0(ControllerActionInvoker invoker, ValueTask`1 actionResultValueTask)\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ControllerActionInvoker.<InvokeNextActionFilterAsync>g__Awaited|10_0(ControllerActionInvoker invoker, Task lastTask, State next, Scope scope, Object state, Boolean isCompleted)\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ControllerActionInvoker.Rethrow(ActionExecutedContextSealed context)\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ControllerActionInvoker.Next(State& next, Scope& scope, Object& state, Boolean& isCompleted)\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ControllerActionInvoker.<InvokeInnerFilterAsync>g__Awaited|13_0(ControllerActionInvoker invoker, Task lastTask, State next, Scope scope, Object state, Boolean isCompleted)\n   at Microsoft.AspNetCore.Mvc.Infrastructure.ResourceInvoker.<InvokeNextExceptionFilterAsync>g__Awaited|26_0(ResourceInvoker invoker, Task lastTask, State next, Scope scope, Object state, Boolean isCompleted)",
};

export interface RequestErrorItem {
  errorkey: string;
  message: string;
  arguments: string | null;
  errorCodeDto: ErrorCodeDto | null;
  propertyname: string;
}
export interface RequestErrorBody {
  code: number;
  errors: RequestErrorItem[];
  traceid: string | null;
  stacktrace: string;
}

const duration401Notification = 10; // seconds

const throttled401Notification = throttle(async (errorResponse) => {
  const responseText = await errorResponse.text();
  const error: RequestErrorBody = JSON.parse(responseText);

  error.errors?.forEach((item) => {
    if (item.errorkey !== null) {
      globalErrorDisplay(error.code, item.errorkey, false);
    }
  });
}, duration401Notification * 1000);

export const errorHandlingMiddleware: Middleware[] = [
  {
    post: async (ctx: ResponseContext): Promise<void> => {
      if (ctx.response.status === 401 && ctx.url.endsWith("/Login")) {
        notification.error({
          message: i18n.t<string>("error.beGlobal.auth.no-access"),
          description: i18n.t<string>("error.beGlobal.auth.login-failed"),
          duration: 10,
        });
        return;
      }

      if (ctx.response.status === 401) {
        return fetchApiRefreshTokenHandler({
          ctx,
          asyncTokenRefresher: () =>
            store.dispatch.auth.refreshAccessToken(undefined),
        }).catch((error) => {
          console.error(error);

          // RefreshToken request errors aren't handled globally, but here:
          throttled401Notification(error);
        });
      } else if (ctx.response.status === 403) {
        notification.error({
          message: i18n.t<string>("error.beGlobal.error.common.forbidden"),
          duration: 10,
        });
      }

      if (
        ctx.response.status >= 400 &&
        !ctx.url.endsWith("/Login") &&
        !ctx.url.endsWith("/RefreshToken")
      ) {
        try {
          const responseText = await ctx.response.text();

          if (responseText === "") {
            notification.error({
              message: i18n.t<string>("error.common.errorsTitle"),
              duration: 10,
            });
          } else {
            const error: RequestErrorBody = JSON.parse(responseText);

            error.errors?.forEach((item) => {
              // Only handle errors that are not attached to a certain field
              // Field errors get handled in CustomForm
              if (
                (!item.propertyname || item.propertyname === "") &&
                item.errorkey !== null
              ) {
                globalErrorDisplay(
                  error.code,
                  item.errorkey,
                  false,
                  item.arguments
                );
              }
            });
          }
        } catch (error) {
          console.error(error);
        }
      }

      return Promise.resolve();
    },
  },
];
